import { nil, NO, UIColor, UIRoute, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHContractStore } from "../Core/RHContractStore"
import { RHCore } from "../Core/RHCore"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDate } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons, RHColors, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHContractsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import { RHContractEntry } from "../SocketClientFunctions"
import { ContractEditDialogView } from "./ContractEditDialogView"
import { ContractEditDialogState } from "./ContractEditDialogState"

// ─── Contracts View Controller ────────────────────────────────────────────────

export class ContractsViewController extends UIViewController {
    _contractLoadSequence = 0
    
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header4
    ).configuredWithObject({
        text: "Lepingud",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    readonly dealsDataView = new RHDataView<RHContractEntry>().configuredWithObject({
        descriptors: [
            CBDataViewCellDescriptorWithObject({
                title: "Hanke viitenumber",
                keyPath: "procurement.referenceNumber",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = dataItem.procurement?.referenceNumber?.toString() || "-"
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Hange",
                keyPath: "procurement.title",
                buttonWasPressed: (_cellView, dataItem) => {
                    const procurementId = dataItem.procurement?._id
                    if (!procurementId) {
                        return
                    }
                    UIRoute.currentRoute
                        .routeWithViewControllerComponent(ProcurementDetailsViewController, {
                            id: procurementId,
                            backTo: ContractsViewController.routeComponentName
                        })
                        .routeByRemovingComponentNamed(ContractsViewController.routeComponentName)
                        .apply()
                },
                initCell: cellView => cellView.configureAsTextLinkWithNormalTextColor(RHColor.textBlack),
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.text = RHTextSanitizer.escapedHTML(
                        dataItem.procurement?.title ?? "-"
                    )
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Töövõtja",
                keyPath: "offererCompany.name",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = dataItem.offererCompany?.name ?? "-"
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Osad",
                keyPath: "winningPartNumbers",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (!RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(
                        dataItem.procurementPartCount
                    )) {
                        cellView.titleLabel!.text = ""
                        return
                    }
                    const partNumbers = dataItem.winningPartNumbers ?? []
                    cellView.titleLabel!.text = partNumbers.length > 0
                                                ? partNumbers.join(", ")
                                                : "-"
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Lepingu maksumus",
                keyPath: "agreedAmount",
                allowSorting: YES,
                dataType: "number" as const,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (dataItem.agreedAmount != null) {
                        cellView.titleLabel!.text = RHEuroFormatter.formattedCurrency(dataItem.agreedAmount, {
                            currency: dataItem.currency ?? "EUR",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                        })
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Lepingu number",
                keyPath: "contractNumber",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.titleLabel!.text = dataItem.contractNumber ?? "-"
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Lepingu sõlmimise kuupäev",
                keyPath: "signedAt",
                allowSorting: YES,
                dataType: "string" as const,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (dataItem.signedAt) {
                        cellView.titleLabel!.text = formatRHDate(new Date(dataItem.signedAt))
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Otsuse kuupäev",
                keyPath: "createdAt",
                allowSorting: YES,
                dataType: "string" as const,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    if (dataItem.createdAt) {
                        cellView.titleLabel!.text = formatRHDate(new Date(dataItem.createdAt))
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Staatus",
                keyPath: "isCancelled",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {
                    const mode = ContractEditDialogState.modeForEntry(dataItem)
                    if (mode === "cancelled") {
                        if (dataItem.wasSignedBeforeCancellation) {
                            cellView.titleLabel!.text = "Tühistatud"
                        }
                        else {
                            cellView.titleLabel!.text = "Ei sõlmitud"
                        }
                    }
                    else if (mode === "completed") {
                        cellView.titleLabel!.text = "Lõpetatud"
                    }
                    else if (mode === "signed") {
                        cellView.titleLabel!.text = "Sõlmitud"
                    }
                    else {
                        cellView.titleLabel!.text = "Ootel"
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "",
                keyPath: "",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView(),
                updateCell: (cellView, dataItem) => {
                    const actionButtonView = cellView.customView as RHTableActionButtonView
                    actionButtonView.text = this._actionTitleForContract(dataItem)
                    actionButtonView.button.variant = this._actionVariantForContract(dataItem)
                    actionButtonView.buttonWasPressed = () => {
                        this._openContractEditDialog(dataItem)
                    }
                }
            })
        ]
    }).addedAsSubviewToView(this.view)
    
    readonly bottomSpacerView = new UIView().addedAsSubviewToView(this.view)
    
    
    constructor(view: UIView) {
        super(view)

        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })
        this._applyTableColumnLayout()
    }


    private _applyTableColumnLayout() {
        this.dealsDataView.tableHeaderView.configureWithObject({
            cellWeights: RHContractsViewTokens.columnWeights,
            intrinsicContentHeight: () => 64
        })
        this.dealsDataView.tableView.configureWithObject({
            cellWeights: RHContractsViewTokens.columnWeights
        })
        this._configureTableHeaderCells()
        this.dealsDataView.tableHeaderView.setNeedsLayout()
        this.dealsDataView.tableView.setNeedsLayout()
    }

    private _configureTableHeaderCells() {
        for (const headerCell of this.dealsDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHContractsViewTokens.multilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHContractsViewTokens.multilineHeaderLineHeight
        }
    }


    static override readonly routeComponentName = "deals"
    static override readonly ParameterIdentifierName = {}


    override async viewWillAppear() {
        await super.viewWillAppear()
        RHCore.sharedInstance.contractStore.subscribe(this, () => {
            void this._loadContracts()
        })
    }


    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHCore.sharedInstance.contractStore.unsubscribe(this)
    }
    
    
    override async viewDidAppear() {
        await super.viewDidAppear()
        
        this.dealsDataView.data = []
        this.dealsDataView.noDataMessage = "Lepinguid ei leitud."
        
        await this._loadContracts()

        this.view.setNeedsLayout()
    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (
            event.name === RHCore.broadcastEventName.activeCompanyDidChange ||
            event.name === RHCore.broadcastEventName.applicationModeDidChange
        ) {
            this.dealsDataView.data = []
            this._loadContracts().then(() =>
                this.view.setNeedsLayout()
            ).catch(error => console.error(
                "Error reloading contracts after company or mode change:", error
            ))
        }
    }


    async _loadContracts() {
        const loadSequence = ++this._contractLoadSequence
        const core = RHCore.sharedInstance
        this.dealsDataView.noDataMessage = "Lepinguid ei leitud."
        try {
            let contracts: RHContractEntry[] = []
            if (core.applicationMode === "offerer") {
                contracts = await core.contractStore.retrieveMyContractsForCurrentOfferer()
            }
            else if (core.applicationMode === "procurer") {
                contracts = await core.contractStore.retrieveMyContractsForCurrentProcurer()
            }
            if (loadSequence === this._contractLoadSequence) {
                this.dealsDataView.data = contracts
            }
        }
        catch (error) {
            console.error("Error loading contracts:", error)
            if (loadSequence === this._contractLoadSequence) {
                this.dealsDataView.data = []
                this.dealsDataView.noDataMessage = "Viga lepingute laadimisel."
            }
        }
    }
    
    
    _openContractEditDialog(entry: RHContractEntry) {
        const editView = new ContractEditDialogView().configuredWithObject({
            isReadOnly: !this._contractIsEditableByCurrentUser(entry)
        })
        editView.populateFromEntry(entry)
        
        const shower = CBDialogViewShower.showDialog(editView, nil, NO)
        
        // noinspection JSConstantReassignment
        editView.saveButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            await editView.saveContractDetailsInDialog(shower, async () => {
                await this._saveContractDetails({ contractId: entry._id, ...editView.contractDetails })
            })
        }
        
        // noinspection JSConstantReassignment
        editView.cancelContractButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            if (!editView.allowsCancellation) {
                return
            }
            editView.replaceDialogWithCancellationConfirmation(
                shower,
                async () => {
                    await this._saveContractDetails({
                        contractId: entry._id,
                        isCancelled: YES,
                        nonConclusionReason: editView.enteredNonConclusionReason
                    })
                }
            )
        }

        editView.completeContractButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            if (!editView.allowsCompletion || !editView.completeContractButton.enabled) {
                return
            }
            editView.completeContractButton.enabled = NO
            editView._updateErrorMessage()
            try {
                await RHCore.sharedInstance.contractStore.markContractCompleted(entry._id)
                shower.dialogView.dismiss()
                await this._loadContracts()
            }
            catch (error) {
                console.error("Error marking contract completed:", error)
                editView._showSaveError(error)
            }
            finally {
                editView.completeContractButton.enabled = YES
            }
        }

        // noinspection JSConstantReassignment
        editView.closeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            shower.dialogView.dismiss()
        }
    }


    _actionTitleForContract(entry: RHContractEntry) {
        if (!this._contractIsEditableByCurrentUser(entry)) {
            return "Vaata"
        }
        if (entry.contractNumber || entry.signedAt) {
            return "Muuda"
        }
        return "Sisesta"
    }


    _actionVariantForContract(entry: RHContractEntry) {
        if (!this._contractIsEditableByCurrentUser(entry)) {
            return RHButtons.tableActionVariant
        }
        if (entry.contractNumber || entry.signedAt) {
            return RHButtons.exitUtilityVariant
        }
        return RHButtons.tableActionVariant
    }


    _contractIsEditableByCurrentUser(contract: RHContractEntry): boolean {
        return RHCore.sharedInstance.applicationMode === "procurer" &&
            contract.isProcurementEditableByCurrentUser === YES &&
            ContractEditDialogState.allowsSaving(ContractEditDialogState.modeForEntry(contract))
    }
    
    
    async _saveContractDetails(input: Parameters<RHContractStore["updateContractDetails"]>[0]) {
        const updatedEntry = await RHCore.sharedInstance.contractStore.updateContractDetails(input)
        const currentData = this.dealsDataView.data
        const updatedData = currentData.map(item => {
            if (item._id === updatedEntry._id) {
                return updatedEntry
            }
            return item
        })
        this.dealsDataView.data = updatedData
    }
    
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
    }
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(padding)
        
        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.dealsDataView)
            .rectangleForNextRow(padding, this.bottomSpacerView)
            .assignedAsFrameOfView(this.bottomSpacerView)
    }
    
}
